/**
 * Template JavaScript Integration
 * Integrates AI Agency template scripts with Laravel Vite
 */

// Core dependencies - jQuery must be first and globally available
import jQuery from 'jquery';

// Make jQuery globally available BEFORE any other imports
window.$ = window.jQuery = jQuery;

// Ensure jQuery is fully loaded and available
if (typeof window.jQuery === 'undefined') {
    console.error('jQuery failed to load');
}

// Bootstrap (requires jQuery to be global)
import 'bootstrap';

// GSAP and modules
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Swiper } from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Configure Swiper modules
Swiper.use([Navigation, Pagination, Autoplay]);

// Make GSAP globally available for template plugins
window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;
window.Swiper = Swiper;

// Define globals for compatibility
window.Keyboard = window.Keyboard || {};

// Import template JavaScript files directly
const loadPlugins = async () => {
    // Ensure jQuery is available in global scope
    if (!window.jQuery) {
        console.error('jQuery not available for plugins');
        return;
    }
    
    try {
        // Import legacy plugins - Vite will handle bundling them correctly
        // These use ?url to get the file path and then load as scripts
        const waypointsUrl = (await import('./template/jquery.waypoints.min.js?url')).default;
        const wowUrl = (await import('./template/wow.min.js?url')).default;
        const validatorUrl = (await import('./template/validator.min.js?url')).default;
        const counterupUrl = (await import('./template/jquery.counterup.min.js?url')).default;
        const magnificUrl = (await import('./template/jquery.magnific-popup.min.js?url')).default;
        const magiccursorUrl = (await import('./template/magiccursor.js?url')).default;
        const parallaxieUrl = (await import('./template/parallaxie.js?url')).default;
        const smoothScrollUrl = (await import('./template/SmoothScroll.js?url')).default;
        const splitTextUrl = (await import('./template/SplitText.js?url')).default;
        const functionUrl = (await import('./template/function.js?url')).default;

        // Load them in order
        await loadScript(waypointsUrl);
        await loadScript(wowUrl);
        await loadScript(validatorUrl);
        await loadScript(counterupUrl);
        await loadScript(magnificUrl);
        await loadScript(magiccursorUrl);
        await loadScript(parallaxieUrl);
        await loadScript(smoothScrollUrl);
        await loadScript(splitTextUrl);
        await loadScript(functionUrl);
        
        console.log('All template plugins loaded successfully');
    } catch (error) {
        console.error('Error loading template plugins:', error);
    }
};

// Helper function to load a script from URL
const loadScript = (url) => {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.type = 'text/javascript';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load ${url}`));
        document.head.appendChild(script);
    });
};

// Load plugins and initialize components
const initializeComponents = async () => {
    try {
        // Load all template plugins first
        await loadPlugins();
        
        // Initialize WOW if available
        if (typeof WOW !== 'undefined') {
            new WOW().init();
        }
        
        // Initialize Prisma Soft customizations
        if (window.PrismaSoft) {
            PrismaSoft.initPerformanceMonitoring();
            PrismaSoft.initContactForms();
            PrismaSoft.initLanguageSwitcher();
            PrismaSoft.initTemplate();
        }
    } catch (error) {
        console.error('Error initializing components:', error);
    }
};

// Start component initialization after DOM is ready
jQuery(document).ready(function() {
    initializeComponents();
});

// Prisma Soft customizations
window.PrismaSoft = {
    // WhatsApp integration
    initWhatsApp: function(number) {
        console.log('Initializing WhatsApp integration for:', number);
    },
    
    // Form enhancements
    initContactForms: function() {
        console.log('Initializing contact forms with Laravel validation');
    },
    
    // Language switcher
    initLanguageSwitcher: function() {
        console.log('Initializing language switcher');
    },
    
    // Performance monitoring for African infrastructure
    initPerformanceMonitoring: function() {
        if ('connection' in navigator) {
            const connection = navigator.connection;
            if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
                // Disable heavy animations on slow connections
                document.body.classList.add('reduce-motion');
            }
        }
    },

    // Initialize template functionality
    initTemplate: function() {
        // Template initialization is now handled by imports
        console.log('Template scripts loaded via Vite modules');
    }
};

// Export for global usage
window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;
window.Swiper = Swiper;